import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Globe, Heart, Download, User, Search, Filter, TrendingUp, Clock, Star, Share2, Plus } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import GlassCard from "@/components/GlassCard";
import NeonButton from "@/components/NeonButton";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sample community presets (will be replaced with real data)
const samplePresets = [
  {
    id: "1",
    name: "Deep Focus Study",
    description: "Perfect for long study sessions. Alpha waves with pink noise.",
    author: "MindMaster",
    likes: 342,
    downloads: 1205,
    category: "Focus",
    settings: { frequency: 528, binaural: 12, noise: "pink", volume: 60 },
    createdAt: "2025-01-10"
  },
  {
    id: "2",
    name: "Lucid Dream Inducer",
    description: "Theta frequencies designed to enhance dream awareness.",
    author: "DreamWeaver",
    likes: 567,
    downloads: 2341,
    category: "Sleep",
    settings: { frequency: 432, binaural: 6, noise: "brown", volume: 45 },
    createdAt: "2025-01-08"
  },
  {
    id: "3",
    name: "Anxiety Relief",
    description: "Calming combination to ease stress and anxiety.",
    author: "CalmVibes",
    likes: 891,
    downloads: 3456,
    category: "Wellness",
    settings: { frequency: 396, binaural: 8, noise: "pink", volume: 55 },
    createdAt: "2025-01-05"
  },
  {
    id: "4",
    name: "Morning Energy Boost",
    description: "Wake up energized with beta frequencies.",
    author: "EnergyFlow",
    likes: 234,
    downloads: 987,
    category: "Energy",
    settings: { frequency: 639, binaural: 18, noise: "white", volume: 70 },
    createdAt: "2025-01-12"
  },
  {
    id: "5",
    name: "Creative Flow State",
    description: "Alpha-theta border for enhanced creativity.",
    author: "ArtistMind",
    likes: 456,
    downloads: 1567,
    category: "Creativity",
    settings: { frequency: 528, binaural: 10, noise: "pink", volume: 50 },
    createdAt: "2025-01-11"
  },
  {
    id: "6",
    name: "Pain Management",
    description: "174Hz solfeggio frequency for natural pain relief.",
    author: "HealingSound",
    likes: 678,
    downloads: 2109,
    category: "Healing",
    settings: { frequency: 174, binaural: 4, noise: "brown", volume: 40 },
    createdAt: "2025-01-09"
  }
];

const categories = ["All", "Focus", "Sleep", "Wellness", "Energy", "Creativity", "Healing", "Meditation"];

const Discover = () => {
  const { isAuthenticated, user } = useAuth();
  const [presets, setPresets] = useState(samplePresets);
  const [filteredPresets, setFilteredPresets] = useState(samplePresets);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sortBy, setSortBy] = useState("popular");
  const [likedPresets, setLikedPresets] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load community presets
  useEffect(() => {
    loadCommunityPresets();
  }, []);

  // Filter and sort presets
  useEffect(() => {
    let result = [...presets];

    // Filter by category
    if (selectedCategory !== "All") {
      result = result.filter(p => p.category === selectedCategory);
    }

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p => 
        p.name.toLowerCase().includes(query) || 
        p.description.toLowerCase().includes(query) ||
        p.author.toLowerCase().includes(query)
      );
    }

    // Sort
    switch (sortBy) {
      case "popular":
        result.sort((a, b) => b.likes - a.likes);
        break;
      case "downloads":
        result.sort((a, b) => b.downloads - a.downloads);
        break;
      case "recent":
        result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      default:
        break;
    }

    setFilteredPresets(result);
  }, [presets, selectedCategory, searchQuery, sortBy]);

  const loadCommunityPresets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/community/presets`);
      if (response.data && response.data.length > 0) {
        setPresets(response.data);
      }
    } catch (error) {
      // Use sample data if API fails
      console.log("Using sample community presets");
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (presetId) => {
    if (!isAuthenticated) {
      toast.error("Sign in to like presets");
      return;
    }

    if (likedPresets.includes(presetId)) {
      setLikedPresets(prev => prev.filter(id => id !== presetId));
      setPresets(prev => prev.map(p => 
        p.id === presetId ? { ...p, likes: p.likes - 1 } : p
      ));
    } else {
      setLikedPresets(prev => [...prev, presetId]);
      setPresets(prev => prev.map(p => 
        p.id === presetId ? { ...p, likes: p.likes + 1 } : p
      ));
      toast.success("Added to favorites");
    }
  };

  const handleDownload = (preset) => {
    // Save to local presets
    const savedPresets = JSON.parse(localStorage.getItem('subspace_presets') || '[]');
    const newPreset = {
      id: `community-${preset.id}-${Date.now()}`,
      name: preset.name,
      settings: preset.settings,
      source: "community",
      author: preset.author,
      createdAt: new Date().toISOString()
    };
    savedPresets.push(newPreset);
    localStorage.setItem('subspace_presets', JSON.stringify(savedPresets));
    
    setPresets(prev => prev.map(p => 
      p.id === preset.id ? { ...p, downloads: p.downloads + 1 } : p
    ));
    
    toast.success(`"${preset.name}" saved to your presets`);
  };

  const handleShare = async (preset) => {
    const shareUrl = `${window.location.origin}/discover?preset=${preset.id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: preset.name,
          text: preset.description,
          url: shareUrl
        });
      } catch (e) {
        // User cancelled
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard");
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      Focus: "from-cyan-500 to-blue-600",
      Sleep: "from-indigo-500 to-purple-600",
      Wellness: "from-pink-500 to-rose-600",
      Energy: "from-yellow-500 to-orange-600",
      Creativity: "from-purple-500 to-pink-600",
      Healing: "from-green-500 to-emerald-600",
      Meditation: "from-violet-500 to-indigo-600"
    };
    return colors[category] || "from-gray-500 to-gray-600";
  };

  return (
    <div className="min-h-screen pt-20 md:pt-8 pb-32 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <p className="tagline-shimmer text-xs font-semibold uppercase mb-3" data-testid="discover-tagline">
            Expand Your Mind.
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            <span className="text-neon">Discover</span>
          </h1>
          <p className="text-gray-400">
            Explore community-created sound presets and share your own
          </p>
        </motion.div>

        {/* Search and Filters */}
        <GlassCard hover={false} className="mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                placeholder="Search presets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="discover-search"
                className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-gray-600 focus:border-violet-500/50 focus:outline-none"
              />
            </div>

            {/* Sort */}
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-gray-500" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:border-violet-500/50 focus:outline-none"
              >
                <option value="popular">Most Popular</option>
                <option value="downloads">Most Downloads</option>
                <option value="recent">Most Recent</option>
              </select>
            </div>
          </div>

          {/* Categories */}
          <div className="flex flex-wrap gap-2 mt-4">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={cn(
                  "px-4 py-1.5 rounded-full text-sm transition-all",
                  selectedCategory === cat
                    ? "bg-violet-600 text-white"
                    : "bg-white/5 text-gray-400 hover:bg-white/10"
                )}
              >
                {cat}
              </button>
            ))}
          </div>
        </GlassCard>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { icon: Globe, label: "Community Presets", value: presets.length },
            { icon: TrendingUp, label: "Total Downloads", value: presets.reduce((a, p) => a + p.downloads, 0).toLocaleString() },
            { icon: Heart, label: "Total Likes", value: presets.reduce((a, p) => a + p.likes, 0).toLocaleString() }
          ].map((stat, i) => (
            <GlassCard key={i} hover={false} className="text-center py-4">
              <stat.icon size={20} className="mx-auto text-violet-400 mb-2" />
              <div className="text-xl font-bold text-white">{stat.value}</div>
              <div className="text-xs text-gray-500">{stat.label}</div>
            </GlassCard>
          ))}
        </div>

        {/* Presets Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-gray-400">Loading presets...</p>
          </div>
        ) : filteredPresets.length === 0 ? (
          <GlassCard hover={false} className="text-center py-12">
            <Search size={48} className="mx-auto text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-400 mb-2">No presets found</h3>
            <p className="text-sm text-gray-500">Try adjusting your search or filters</p>
          </GlassCard>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPresets.map((preset, i) => (
              <motion.div
                key={preset.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <GlassCard className="h-full">
                  {/* Category Badge */}
                  <div className="flex items-center justify-between mb-3">
                    <span className={cn(
                      "text-xs px-2 py-1 rounded-full bg-gradient-to-r text-white",
                      getCategoryColor(preset.category)
                    )}>
                      {preset.category}
                    </span>
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Clock size={12} />
                      {new Date(preset.createdAt).toLocaleDateString()}
                    </span>
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-lg font-semibold text-white mb-1">{preset.name}</h3>
                  <p className="text-sm text-gray-400 mb-3 line-clamp-2">{preset.description}</p>

                  {/* Author */}
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
                      <User size={12} className="text-white" />
                    </div>
                    <span className="text-sm text-gray-400">{preset.author}</span>
                  </div>

                  {/* Settings Preview */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="text-xs px-2 py-1 rounded bg-white/5 text-gray-400">
                      {preset.settings.frequency}Hz
                    </span>
                    <span className="text-xs px-2 py-1 rounded bg-white/5 text-gray-400">
                      {preset.settings.binaural}Hz binaural
                    </span>
                    <span className="text-xs px-2 py-1 rounded bg-white/5 text-gray-400">
                      {preset.settings.noise} noise
                    </span>
                  </div>

                  {/* Stats & Actions */}
                  <div className="flex items-center justify-between pt-3 border-t border-white/10">
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Heart size={14} className={likedPresets.includes(preset.id) ? "fill-red-500 text-red-500" : ""} />
                        {preset.likes}
                      </span>
                      <span className="flex items-center gap-1">
                        <Download size={14} />
                        {preset.downloads}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleLike(preset.id)}
                        className={cn(
                          "p-2 rounded-lg transition-colors",
                          likedPresets.includes(preset.id)
                            ? "bg-red-500/20 text-red-400"
                            : "bg-white/5 text-gray-400 hover:text-red-400"
                        )}
                      >
                        <Heart size={16} className={likedPresets.includes(preset.id) ? "fill-current" : ""} />
                      </motion.button>
                      
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleShare(preset)}
                        className="p-2 rounded-lg bg-white/5 text-gray-400 hover:text-white transition-colors"
                      >
                        <Share2 size={16} />
                      </motion.button>
                      
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleDownload(preset)}
                        data-testid={`download-preset-${preset.id}`}
                        className="p-2 rounded-lg bg-violet-500/20 text-violet-400 hover:bg-violet-500/30 transition-colors"
                      >
                        <Download size={16} />
                      </motion.button>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        )}

        {/* Share Your Own CTA */}
        <GlassCard hover={false} className="mt-12 text-center">
          <div className="max-w-lg mx-auto">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
              <Plus size={28} className="text-white" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Share Your Creations</h3>
            <p className="text-gray-400 mb-4">
              Created an amazing sound combination? Share it with the Subspace community and help others on their journey.
            </p>
            <NeonButton onClick={() => toast.info("Publishing feature coming soon!")}>
              Publish a Preset
            </NeonButton>
          </div>
        </GlassCard>
      </div>
    </div>
  );
};

export default Discover;
